import { useMemo, useState } from 'react';
import { Select } from '@customerio/pluma-components/react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useAttributeNameSearch } from '@/services/db/attribute/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { isEmptyString } from '@/utils/string';
import type { AttributeNamePickerProps } from '../components/attribute-row/attribute-row';

/**
 * The picker for a profile attribute's name, shared by every surface that
 * filters on profile attributes.
 *
 * Shared because the names come from one place — the workspace's profile
 * attributes — whatever is being filtered. A surface with its own name source
 * (a collection's columns, say) supplies its own picker instead; this is only
 * for the profile-attribute ones.
 */

type AttributeNameOption = { value: string; label: string };

/** The picker's options, the term it is being searched by, and its state. */
export type ProfileAttributeNames = {
  options: AttributeNameOption[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isSearching: boolean;
};

/**
 * The attribute-name options: the environment's bounded sent-attributes list
 * as the local seed, plus debounced server search. Searched names accumulate
 * so a picked search result keeps its option after the search clears.
 *
 * The merged pool is narrowed here by case-insensitive substring on the typed
 * term. The row controls the select's search input, which turns Pluma's own
 * client-side filter off (`shouldFilter` is false whenever `searchInputValue`
 * or `onSearchInput` is supplied), so filtering is this hook's job. The stored
 * name is appended after filtering, so a saved condition renders its label
 * instead of going blank while a non-matching term is typed.
 *
 * A typed term that no option matches exactly leads the list as its own
 * option, so any attribute name can be specified rather than only picked.
 * Because the term externally controls the select's input, the picker clears
 * it on select and on close: a leftover term is invisible in the reopened
 * input yet still seeds that custom option, so later keystrokes would append
 * to it.
 */
export function useProfileAttributeNames(selectedField: string): ProfileAttributeNames {
  const workspaceId = requireWorkspaceId();
  const environment = useEnvironmentOptional();
  const localAttributes = useMemo(() => environment.data?.environment?.sent_attributes ?? [], [environment.data]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm] = useDebouncedValue(searchTerm, 300);
  const search = useAttributeNameSearch(workspaceId, debouncedTerm);

  const [searchedAttributes, setSearchedAttributes] = useState<readonly string[]>([]);
  const searchMatches = search.data;
  if (searchMatches != null) {
    const merged = [...new Set([...searchedAttributes, ...searchMatches])];
    if (merged.length !== searchedAttributes.length) {
      setSearchedAttributes(merged);
    }
  }

  const options: AttributeNameOption[] = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const names = new Set(
      [...localAttributes, ...searchedAttributes].filter((name) => name.toLowerCase().includes(needle)),
    );
    const custom = !isEmptyString(searchTerm) && !names.has(searchTerm) ? [searchTerm] : [];
    if (!isEmptyString(selectedField)) {
      names.add(selectedField);
    }
    return [...custom, ...names].map((name) => ({ value: name, label: name }));
  }, [localAttributes, searchedAttributes, selectedField, searchTerm]);

  return { options, searchTerm, setSearchTerm, isSearching: search.isFetching };
}

type ProfileAttributeNamePickerProps = AttributeNamePickerProps & {
  /** The options and search state, from `useProfileAttributeNames`. */
  names: ProfileAttributeNames;
};

/** The select a profile-attribute row names its attribute with. */
export function ProfileAttributeNamePicker({
  field,
  error,
  isDisabled,
  onFieldChange,
  onTouched,
  names,
}: ProfileAttributeNamePickerProps) {
  return (
    <Select
      data-testid="dryad-attribute-name"
      ariaLabel="Profile attribute"
      size="sm"
      placeholder="Profile attribute"
      options={names.options}
      value={field === '' ? undefined : field}
      error={error}
      isDisabled={isDisabled}
      isSearchable
      searchPlaceholder="Find or specify an attribute"
      searchInputValue={names.searchTerm}
      onSearchInput={(event) => names.setSearchTerm(event.target.value)}
      isLoading={names.isSearching}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          onTouched();
          names.setSearchTerm('');
        }
      }}
      onChange={(value: string | null) => {
        names.setSearchTerm('');
        if (value != null) {
          onFieldChange(value);
        }
      }}
    />
  );
}
